/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, {
  useState,
  useMemo,
  useRef,
  useCallback,
  useEffect,
} from 'react';
import { View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import TextFieldView from '@airasia-phoenix/react-native/components/TextFieldView';
import { pd } from '@airasia-phoenix/react-native/utils/utils';
import type { BottomSheetRefs } from '@airasia-phoenix/react-native/components/BottomSheetView';
import { getCurrentBreakpoint } from '@airasia-rn/ui/utils';
import { getColor } from '@airasia-phoenix/react-native/utils/utils';

import GuestBottomSheetView from './GuestBottomSheetView';
import { getGuestSummary } from './utils';
import { RoomConfig, GuestSelectionConfig } from '../../types';
import { defaultGuestSelectionConfig } from './constants';
import { useTranslation } from 'react-i18next';

const GuestFieldView = ({
  rooms = [],
  onConfirm,
  config = defaultGuestSelectionConfig,
}: {
  rooms: RoomConfig[];
  onConfirm: (rooms: RoomConfig[]) => void;
  config?: GuestSelectionConfig;
}) => {
  const bottomSheetViewRef = useRef<BottomSheetRefs>(null);
  const anchorRef = useRef<View>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const isDesktopWeb = getCurrentBreakpoint() > 1;
  const { t } = useTranslation('hotels');
  const displayText = useMemo(() => getGuestSummary(rooms, t), [rooms, t]);

  useEffect(() => {
    // When viewport/device mode changes, close stale presentation to avoid showing wrong container.
    if (isDesktopWeb && isSheetOpen) {
      bottomSheetViewRef.current?.toggleBottomSheetView();
      setIsSheetOpen(false);
    } else if (!isDesktopWeb && isModalVisible) {
      setIsModalVisible(false);
    }
  }, [isDesktopWeb, isSheetOpen, isModalVisible]);

  const openPicker = useCallback(() => {
    // For mobile web, use bottom sheet instead of modal
    if (isDesktopWeb) {
      setIsModalVisible(true);
    } else {
      setIsSheetOpen(true);
      bottomSheetViewRef.current?.toggleBottomSheetView();
    }
  }, [isDesktopWeb]);

  const handleClose = useCallback(() => {
    setIsModalVisible(false);
    setIsSheetOpen(false);
  }, []);

  return (
    <>
      <View ref={anchorRef} collapsable={false} style={styles.fieldAnchor}>
        <TextFieldView
          testID="hotel-guest-field-trigger"
          label={t('searchHotelPage.guestSelection.title')}
          value={displayText}
          onPress={openPicker}
          startIcon={{
            icon: pd.icon.main.social.people_outline,
            tint: getColor(pd.sys.colour.content_secondary),
          }}
          endIcon={{
            icon: isModalVisible
              ? pd.icon.main.navigation.arrow_drop_up
              : pd.icon.main.navigation.arrow_drop_down,
            tint: getColor(pd.sys.colour.content_primary),
          }}
          editable={false}
          disabled={false}
        />
      </View>

      <GuestBottomSheetView
        isSheetOpen={isSheetOpen}
        initialRooms={rooms}
        onConfirm={onConfirm}
        bottomSheetViewRef={bottomSheetViewRef}
        onRequestCloseModal={handleClose}
        onSheetDidClose={handleClose}
        isModalVisible={isModalVisible}
        anchorRef={anchorRef}
        config={config}
      />
    </>
  );
};

const styles = StyleSheet.create({
  fieldAnchor: {
    width: '100%',
  },
});

export default GuestFieldView;
